import React, { useLayoutEffect, useRef, useState } from "react";
import "./SecondaryPanel.css";
import InfoButton from "./ui/InfoButton";
import NoButton from "./ui/NoButton";
import { useMenu } from "../context/MenuContext";
import { useDeviceDetection } from "../../../hooks/useDeviceDetection";
import { useAutoClosePanel } from "../../../hooks/useAutoClosePanel";

const SecondaryPanel = ({
  isOpen = true,
  parent,
  childrenTags,
  onClose,
  left,
  top,
  onInfoClick,
  checkHasDefinition, // Récupération de la fonction
}) => {
  const { isMobile } = useDeviceDetection();
  const { filters, toggleFilter, viewMode, colorMap, resetFilters } = useMenu();

  const panelRef = useRef(null);
  
  const [adjustedTop, setAdjustedTop] = useState(top);

  useLayoutEffect(() => {
    if (panelRef.current) {
      const panelHeight = panelRef.current.offsetHeight;
      const maxTop = window.innerHeight - panelHeight - 10;
      setAdjustedTop(Math.max(0, Math.min(top, maxTop)));
    }
  }, [top, childrenTags.length]);
  /**/

  useAutoClosePanel({
    isOpen,
    panelRef,
    onClose,
    timeout: 5000,
  });
  
  /*
        // Lorsque l'on clique sur le parent :
        // - si on l'active (il était inactif) => désactiver tous les enfants, puis activer le parent
        // - si on le désactive (il était actif) => ne rien toucher aux enfants, simplement toggle du parent
        const handleParentClick = () => {
            const parentWasActive = !!filters[parent];
    
            if (!parentWasActive) {
                // on active le parent : d'abord désactiver tous les enfants
                childrenTags.forEach(c => {
                    if (filters[c]) toggleFilter(c);
                });
            }
    
            // toggle parent (active ou désactive selon son état actuel)
            toggleFilter(parent);
        };
    
        // Child click devient un véritable toggle :
        // - si le child est actif => on le désactive
        // - sinon => on désactive le parent (s'il est actif), désactive les autres enfants, puis active celui-ci
        const handleChildClick = (child) => {
            const childWasActive = !!filters[child];
    
            if (childWasActive) {
                // désactiver le child
                toggleFilter(child);
                return;
            }
    
            // activation d'un child
            // désactiver le parent s'il est actif
            if (filters[parent]) toggleFilter(parent);
    
            // désactiver les autres enfants
            childrenTags.forEach(c => {
                if (c !== child && filters[c]) toggleFilter(c);
            });
    
            // activer le child cliqué
            toggleFilter(child);
        };*/

  // Lorsque l'on clique sur le parent :
  // - en grid => reset puis activer le parent (ou le désactiver si déjà actif)
  // - en geo => comportement actuel (si activation du parent on désactive les enfants)
  const handleParentClick = () => {
    const parentWasActive = !!filters[parent];

    if (viewMode === "grid") {
      if (parentWasActive) {
        // simple toggle : désactiver le parent
        toggleFilter(parent);
        return;
      }
      // activation en grid : effacer tout puis activer le parent
      if (typeof resetFilters === "function") {
        resetFilters();
      } else {
        // fallback minimal : désactiver tous les enfants si présent
        childrenTags.forEach((c) => {
          if (filters[c]) toggleFilter(c);
        });
        if (filters[parent]) toggleFilter(parent);
      }
      toggleFilter(parent);
      return;
    }

    // Mode GEO (comportement original)
    if (!parentWasActive) {
      // on active le parent : d'abord désactiver tous les enfants
      childrenTags.forEach((c) => {
        if (filters[c]) toggleFilter(c);
      });
    }
    // toggle parent
    toggleFilter(parent);
  };

  // Child click :
  const handleChildClick = (child) => {
    const childWasActive = !!filters[child];

    if (viewMode === "grid") {
      if (childWasActive) {
        // si déjà actif => simple toggle (désactiver)
        toggleFilter(child);
        return;
      }

      // activation d'un child en grid : effacer tout puis activer le child
      if (typeof resetFilters === "function") {
        resetFilters();
      } else {
        // fallback : désactiver parent et autres children
        if (filters[parent]) toggleFilter(parent);
        childrenTags.forEach((c) => {
          if (c !== child && filters[c]) toggleFilter(c);
        });
      }
      toggleFilter(child);

      // 👉 fermeture automatique seulement sur mobile
      if (isMobile) {
        onClose?.();
      }

      return;
    }

    // Mode GEO (comportement original)
    if (childWasActive) {
      toggleFilter(child);
      return;
    }

    if (filters[parent]) toggleFilter(parent);

    childrenTags.forEach((c) => {
      if (c !== child && filters[c]) toggleFilter(c);
    });

    toggleFilter(child);
  };

  /* --------------------------------------------------
     Render
  -------------------------------------------------- */
  return (
    <div
      className="secondary-panel"
      ref={panelRef}
      style={{ left, top: adjustedTop }}
    >
      <button
        className={`filter-btn ${filters[parent] ? "selected" : "deselected"}`}
        onClick={handleParentClick}
        style={{
          backgroundColor: filters[parent]
            ? "var(--colorFront)"
            : colorMap[parent],
          color: filters[parent] ? "var(--colorBack)" : "var(--colorFront)",
          borderColor: "var(--colorFront)",
        }}
      >
        {parent}
      </button>

      {childrenTags
        .sort((a, b) => {
          //console.log(a,b);
          if (a.split("_").pop() === "Autre") return 1;
          if (b.split("_").pop() === "Autre") return -1;
          if (a.split("_").pop() === "Divers") return 1;
          if (b.split("_").pop() === "Divers") return -1;
          return a.localeCompare(b, "fr", { sensitivity: "base" });
        })
        .map((child) => (
          <div className="secondary-btn-wrapper" key={child}>
            <button
              className={`filter-btn ${
                filters[child] ? "selected" : "deselected"
              }`}
              onClick={() => handleChildClick(child)}
              style={{
                backgroundColor: filters[child]
                  ? "var(--colorFront)"
                  : colorMap[parent],
                color: filters[child]
                  ? "var(--colorBack)"
                  : "var(--colorFront)",
                borderColor: "var(--colorFront)",
              }}
            >
              {child.split("_").pop()}
            </button>

            {checkHasDefinition(child) ? (
              <InfoButton
                onClick={(e) => {
                  // prevent propagation handled in InfoButton
                  onInfoClick && onInfoClick(child, colorMap[parent], parent);
                }}
                isSelected={filters[child]}
                color={colorMap[parent]}
              />
            ) : (
              <NoButton
                //isOpen={openParent === val}
                isOpen={false}
                isSelected={filters[child]}
                color={colorMap[parent]}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default SecondaryPanel;
